// w07 Sokin Bog' — botanical-garden wedding preview card.
//
// The catalog card uses the "outer frame + inner card" structure rendered
// as garden idiom: a sage→cream→blush garland gradient frame surrounds
// the warm-cream inner card. The focal element is a circular photo
// medallion encircled by a botanical wreath (eucalyptus + tiny blooms).
// Names in Cormorant Garamond italic; caption picks up the original's
// "& gathered" tag in a small-caps treatment.
import { Photo, type LayoutCtx } from "./_shared";

const GARDEN_GRADIENT =
  "linear-gradient(135deg, #EFEAE0 0%, #C9D2B1 22%, #EFEAE0 45%, #DABFA6 65%, #EFEAE0 100%)";

export function GardenCascadeLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const captionDate = formatDate(date);
  const leafDeep = "#5E6F3F";
  const leaf = accent;
  const blush = "#D9A593";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: GARDEN_GRADIENT,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TO'Y MAROSIMI · BOG'
        </div>

        {/* Photo medallion wreathed in foliage */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          {/* Wreath SVG behind the photo ring */}
          <svg
            viewBox="0 0 100 100"
            style={{ position: "absolute", inset: "-10%", width: "120%", height: "120%" }}
            aria-hidden
          >
            {/* Half-wreath arcs */}
            <g fill="none" stroke={leafDeep} strokeWidth="0.6" opacity="0.85">
              <path d="M50 8 C 78 8 92 28 92 50 C 92 72 78 92 50 92" />
              <path d="M50 8 C 22 8 8 28 8 50 C 8 72 22 92 50 92" />
            </g>
            {/* Eucalyptus leaves around the ring */}
            {Array.from({ length: 18 }).map((_, i) => {
              const a = (i * (360 / 18) * Math.PI) / 180;
              const cx = 50 + Math.cos(a) * 45;
              const cy = 50 + Math.sin(a) * 45;
              const rot = (i * (360 / 18)) + 90;
              return (
                <ellipse
                  key={i}
                  cx={cx}
                  cy={cy}
                  rx="4.5"
                  ry="2"
                  fill={leaf}
                  opacity="0.9"
                  transform={`rotate(${rot} ${cx} ${cy})`}
                />
              );
            })}
            {/* Tiny blossoms scattered around the wreath */}
            <circle cx="14" cy="38" r="1.6" fill={blush} />
            <circle cx="86" cy="62" r="1.6" fill={blush} />
            <circle cx="68" cy="14" r="1.4" fill={blush} />
            <circle cx="32" cy="86" r="1.4" fill={blush} />
            <circle cx="50" cy="6" r="1.4" fill="#8A98C9" />
            <circle cx="50" cy="94" r="1.4" fill="#8A98C9" />
          </svg>

          {/* Photo ring */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: bg,
              padding: "4%",
              boxSizing: "border-box",
              boxShadow: `inset 0 0 0 1px ${accent}88`,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Photo
                src={photo}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                fallback={
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `radial-gradient(circle at 50% 60%, ${accent}55, ${bg})`,
                    }}
                  />
                }
              />
            </div>
          </div>
        </div>

        {/* "& gathered" mini-flourish */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 11,
            color: accent,
            marginBottom: 6,
            letterSpacing: "0.05em",
          }}
        >
          & gathered
        </div>

        {/* Names */}
        <div style={{ fontFamily: font, fontStyle: "italic", fontSize: 28, lineHeight: 1.05, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          BOG'IMIZGA · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = m[3];
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${day} ${months[month - 1] ?? ""} ${year}`;
}
